<template>
    <base-page>
        <CustomTable
            class=""
            :module-name="moduleName"
            :module-name-singular="moduleNameSingular"
            :response="response"
            :isLoading="isLoading"
            :tableHeaders="tableHeaders"
            :filters="emailTemplateFilters"
            :bulkOperations="emailTemplateBulkOperations"
            :menuOptions="menuOptions"
            :defaultOrdering="defaultOrdering"
            :selectedResultFields="selectedResultFields"
            :shouldReload="shouldReload"
            :shouldClearSelected="shouldClearSelected"
            :bulkOperationResponse="bulkOperationResponse"
            :reorder-bulk-operations="reorderBulkOperations"
            :reorder-filters="reorderFilters"
            :summary-button-reset-id="summaryActionButtonId"
            createModalSize="w-3/4 h-5/6"
            @refetch-results="loadEmailTemplates"
            @clear-filter="clearFilter"
            @clear-all-active-filters="clearAllActiveFilters"
            @change-filters="setFilters"
            @set-filter-options="setFilterOptions"
            @perform-bulk-action="performEmailTemplateBulkAction"
            @toggle-pinned-filter="togglePinnedFilter"
            @toggle-pinned-bulk-operation="togglePinnedBulkOperation"
            @handle-bulk-operation-input-change="handleBulkOperationInputChange"
        >
            <template #create-form="{ events: { closeCreateForm, refetchResults }, fetchFilter }">
                <CreateEmailTemplate
                    :clientOptions="clientOptions"
                    :emailFromOptions="emailFromOptions"
                    :emailToOptions="emailToOptions"
                    @refetch-results="refetchResults(fetchFilter)"
                    @close="closeCreateForm"
                />
            </template>
            <template #details-page="{ events: { closeDetails, refetchResults }, resultRow, userPermissions, fetchFilter }">
                <EmailTemplateDetails
                    :isLoading="isLoading"
                    :email-template="resultRow"
                    :user-permissions="userPermissions" 
                    :clientOptions="clientOptions"
                    :emailFromOptions="emailFromOptions"
                    :emailToOptions="emailToOptions"
                    @close-details="closeDetails"
                    @refetch-results="refetchResults(fetchFilter)"
                />
            </template>
            <template #table-row="{ events: { addSelectedResult, removeSelectedResult, refetchResults, openRowDetails }, selectAll, expandAll, fetchFilter, userPermissions, results, clearedSelectedResults, openRow }">
                <EmailTemplateRow
                    v-for="emailTemplate in results"
                    :key="emailTemplate.id"
                    :selectAll="selectAll"
                    :expandAll="expandAll"
                    :userPermissions="userPermissions"
                    :clearedSelectedResults="clearedSelectedResults"
                    :emailTemplate="emailTemplate"
                    :action-button-reset-id="actionButtonId"
                    :open-row="openRow"
                    @refetch-results="refetchResults(fetchFilter)"
                    @select-row="addSelectedResult"
                    @deselect-row="removeSelectedResult"
                    @open-row-details="openRowDetails"
                    @perform-row-action="performRowAction"
                />
                <tr class="h-12"></tr>
            </template>
            <template #table-footer="{ events: { getNext, goToFirst, getPrev, goToLast, jumpToPage, setPageSizeFilter }, viewingFromNbr, viewingToNbr, count, selectedResults, totalPages, page, results, next, prev }">
                <EmailTemplateFooter
                    :is-loading="isLoading"
                    :viewingFromNbr="viewingFromNbr"
                    :viewingToNbr="viewingToNbr"
                    :count="count"
                    :selectedResults="selectedResults"
                    :numPages="totalPages"
                    :results="results"
                    :page="page"
                    :column-span="columnSpan"
                    :has-next="next"
                    :has-prev="prev"
                    @get-next="getNext"
                    @go-to-first="goToFirst"
                    @get-prev="getPrev"
                    @go-to-last="goToLast"
                    @jump-to-page="jumpToPage"
                    @set-page-size-filter="setPageSizeFilter"
                />
            </template>
        </CustomTable>
    </base-page>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import CustomTable from '@/components/base-components/CustomTable.vue';
import EmailTemplateRow from '@/components/email-templates/EmailTemplateRow.vue';
import EmailTemplateFooter from '@/components/email-templates/EmailTemplateFooter.vue';
import CreateEmailTemplate from '@/components/email-templates/CreateEmailTemplate.vue';
import EmailTemplateDetails from './EmailTemplateDetails.vue';
import { getClients } from '@/services/Client';
import { getEmailTemplatesByQueryParams, getEmailTemplateAddresses } from '@/services/EmailTemplates';

const store = useStore()
const $toast = useToast()
const isLoading = ref(false)
const moduleName = ref('Email Templates')
const moduleNameSingular = ref('Email Template');
const response = ref(null)
const clientOptions = ref([])
const emailFromOptions = ref([])
const emailToOptions = ref([])
const defaultOrdering = ref([])
const shouldReload = ref(false)
const shouldClearSelected = ref(false)
const bulkOperationResponse = ref()
const reorderBulkOperations = ref(false)
const reorderFilters = ref(false)
const displayedEmailTemplate = ref(null)
const campaigns = ref([])
const visibleEditForm = ref(false)
const actionButtonId = ref(null)
const summaryActionButtonId = ref(null)
const selectedResultFields = ref(['id', 'email_template_name', 'mail_to'])
const tableHeaders = ref([
    {
        value: 'email_template',
        label: '',
        class: 'p-0 h-[33px] bg-gray-150 border-b-2',
        orderFieldName: 'email_template_name'
    },
    // {
    //     value: 'address_info',
    //     label: 'Addresses',
    //     class: 'p-0 w-[35%] bg-gray-150 border-b-2',
    //     orderFieldName: 'email_to__email_address'
    // },
    // {
    //     value: 'template_info',
    //     label: 'Template',
    //     class: 'p-0 w-[30%] bg-gray-150 border-b-2',
    // }
])

const menuOptions = reactive({
    controlPanel: {
        width: 'w-[330px]',
        panes: ['filters'],
        defaultFilterTab: 'all',
    },
    loading: {
        rowClass: 'h-28 bg-inherit border-r border-b border-gray-300'
    },
    fetchResultsOnMount: true,
    defaultPageSize: 50,
    openRowType: 'rightPanel',
    openRowIndexOnLoad: 0,
    tableClass: 'w-[600px]',
    clearAllActiveFilters: true,
    quickSearch: false,
    selectAll: false,
    selectAllHeader: false,
    expandAllHeader: false,
    paginationArrows: false,
    totalResults: false,
    pageSize: false,
    refreshButton: false,
    createForm: true,
    bulkOperations: false,
    addFormRows: false,
})

const emailTemplateFilters = reactive({
    client: {
        id: 1,
        name: "client",
        label: "Client",
        value: [],
        defaultValue: [],
        placeholder: 'ABC Aerospace',
        options: [
            {value: "clear", label: "------------"}
        ],
        widget: 'list-box',
        multiSelect: true,
        setOptionsMethod: loadClientOptions.bind(null),
        awaitingOptions: false,
        visible: true,
        pinned: false,
    },
    emailFrom: {
        id: 1,
        name: "email_from",
        label: "Email From",
        value: [],
        defaultValue: [],
        placeholder: 'john.smith@sheddcapital.com',
        options: [
            {value: "clear", label: "------------"}
        ],
        widget: 'list-box',
        multiSelect: true,
        setOptionsMethod: loadEmailAddressOptions.bind(null),
        awaitingOptions: false,
        visible: true,
        pinned: false,
    },
    subject: {
        id: 1,
        name: "subject",
        label: "Subject",
        placeholder: 'To whom it may concern',
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        visible: true,
        pinned: false,
    },
    generalText: {
        id: 1,
        name: "general_text",
        label: "General Text Search",
        placeholder: 'Name, body, subject, email, client, etc.',
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        visible: true,
        pinned: false,
    },
    status: {
        id: 1,
        name: "status",
        label: "Status",
        value: 'active',
        defaultValue: 'active',
        placeholder: '',
        options: [
            {value: null, label: "------------"},
            {value: "active", label: "active"},
            {value: "inactive", label: "inactive"},
        ],
        widget: 'list-box',
        multiSelect: false,
        awaitingOptions: false,
        visible: true,
        pinned: false,
    },
})

const emailTemplateBulkOperations = reactive({})

const companyRowActions = reactive({
    openEditForm: {
        performAction(actionData) {
            displayedEmailTemplate.value = actionData.company
            visibleEditForm.value = true
        }
    },
    // clearZoomInfo: {
    //     async performAction(actionData) {
    //         // console.log('companyRowActions: clearZoomInfo')
    //         const token = store.state.auth.token
    //         const response = await clearZoomInfo(actionData.company.id, token)
    //         handleActionResponse(response, actionData.buttonId, `${actionData.company.company_name} - ${actionData.company.id}: Error Clearing Zoom Info.`)
    //     }
    // },
})

const columnSpan = computed(() => {
    let numHeaders = tableHeaders.value.length
    return menuOptions.selectAllHeader ? numHeaders + 1 : numHeaders
})

function handleBulkActionResponse(actionData, response) {
    if(response.status == 200) {
        $toast.open({
            message:`Completed bulk operation: ${actionData.label}`,
            type: 'success',
            duration: 3000
        })
        if(!actionData.keepSelected) {
            clearSelectedResults()
        }
        reloadCompanies()
        fetchAllOptions()
    } else {
        $toast.open({
            message: `Could not complete bulk operation: ${actionData.label}`,
            type: 'error',
            duration: 10000
        })
    }
    bulkOperationResponse.value = response
}

function clearSelectedResults() {
    shouldClearSelected.value = !shouldClearSelected.value
}

function reloadCompanies() {
    shouldReload.value = !shouldReload.value
}

function setFilters(updatedFilters) {
    emailTemplateFilters.client = updatedFilters.client;
}

function clearFilter(filterKey) {
    if(filterKey.endsWith('.min')) {
        emailTemplateFilters[filterKey.replace('.min', '')].min.value = null
    } else if(filterKey.endsWith('.max')) {
            emailTemplateFilters[filterKey.replace('.max', '')].max.value = null
    } else if(Array.isArray(emailTemplateFilters[filterKey].value)){
        emailTemplateFilters[filterKey].value = []
    } else {
        emailTemplateFilters[filterKey].value = null
    }
}

function clearAllActiveFilters() {
    Object.keys(emailTemplateFilters).forEach((filterKey) => {
        if(emailTemplateFilters[filterKey].min || emailTemplateFilters[filterKey].max) {
            if(emailTemplateFilters[filterKey].min) {
                emailTemplateFilters[filterKey].min.value = null
            } 
            if(emailTemplateFilters[filterKey].max) {
                    emailTemplateFilters[filterKey].max.value = null
            }
        } else if(Array.isArray(emailTemplateFilters[filterKey].value)){
            emailTemplateFilters[filterKey].value = []
        } else {
            emailTemplateFilters[filterKey].value = null
        } 
    })
}

function closeEditForm() {
    visibleEditForm.value = false
    displayedEmailTemplate.value = null
}

function setPinnedFilters(pinnedFilters) {
    pinnedFilters.forEach((pinnedFilter) => {
        let filterToPin = Object.values(emailTemplateFilters).find((filter) => filter.id == pinnedFilter.company_filter_field)
        if(filterToPin) {
            filterToPin.pinned = true
            filterToPin.persisted_id = pinnedFilter.id
        }
    })
    reorderFilters.value = !reorderFilters.value
}

function setPinnedBulkOperations(pinnedBulkOperations) {
    pinnedBulkOperations.forEach((pinnedBulkOperation) => {
        let bulkOperationToPin = Object.values(emailTemplateBulkOperations).find((bulkOperation) => bulkOperation.id == pinnedBulkOperation.company_bulk_operation_field)
        bulkOperationToPin.pinned = true
        bulkOperationToPin.persisted_id = pinnedBulkOperation.id
    })
    reorderBulkOperations.value = !reorderBulkOperations.value
}

function performRowAction(actionData) {
    actionButtonId.value = null
    // console.log('CompanyPage: performRowAction', actionData)
    companyRowActions[actionData.actionKey].performAction(actionData)
}

// function performSummaryAction(actionData) {
//     summaryActionButtonId.value = null
//     // console.log('CompanyPage: performSummaryAction', actionData)
//     emailTemplateSummaryActions[actionData.actionKey].performAction(actionData)
// }

async function setFilterOptions(filterName, queryParams='') {
    // console.log('CompanyPage: setFilterOptions', filterName)
    if(!emailTemplateFilters[filterName].awaitingOptions){
        if(emailTemplateFilters[filterName].setOptionsMethod){
            emailTemplateFilters[filterName].awaitingOptions = true
            emailTemplateFilters[filterName].setOptionsMethod(queryParams)
            setTimeout(() => {
                emailTemplateFilters[filterName].awaitingOptions = false;
            }, 30000)
        }
    }
}

async function loadClientOptions() {
    // console.log('loadClientOptions')
    const token = store.state.auth.token
    const response = await getClients({token: token, endpoint: '?get_all=true'})
    // console.log(response)
    clientOptions.value = response.data.map((client) => {
        return {value: client.id, label: client.client_name}
    })
    emailTemplateFilters.client.options = [{value: "clear", label: "------------"}, ...response.data.map((client) => {
        return {
            label: client.client_name, 
            value: client.id,
        }
    })];
}

async function loadEmailAddressOptions() {
    // console.log('loadEmailAddressOptions')
    const token = store.state.auth.token
    const response = await getEmailTemplateAddresses(token, '')
    // console.log(response)
    emailToOptions.value = [{value: null, label: '------------'}, ...response.data
        .filter((emailAddress) => emailAddress.show_in_email_to == true)
        .map((emailAddress) => { return {
            label: emailAddress.email_address, value: emailAddress.id
        }
    })]
    emailTemplateFilters.emailFrom.options = [{value: "clear", label: '------------'}, ...response.data
        .filter((emailAddress) => emailAddress.show_in_email_to == true)
        .map((emailAddress) => { return {
            label: emailAddress.email_address, value: emailAddress.id
        }
    })]
    // Indicate which from-addresses are approved in the Entra app for outreach-draft
    // (direct Graph) sending. The rest can only be used by the legacy SES flow.
    emailFromOptions.value = [{value: null, label: '------------'}, ...response.data
        .filter((emailAddress) => emailAddress.show_in_email_from == true)
        .map((emailAddress) => { return {
            label: `${emailAddress.email_address}${emailAddress.available_for_outreach_drafts ? '  ✓ outreach-ready' : ''}`,
            value: emailAddress.id
        }
    })]
}

async function loadEmailTemplates(fetchFilter='') {
    // console.log('loadEmailTemplates')
    // console.log(fetchFilter)
    isLoading.value = true;
    response.value = null;
    const token = store.state.auth.token
    response.value = await getEmailTemplatesByQueryParams(token, fetchFilter);
    // if(displayedEmailTemplate.value) {
    //     displayedEmailTemplate.value = response.value.data.results.find((company) => company.id == displayedEmailTemplate.value.id)
    // }
    isLoading.value = false;
}

async function loadPinnedFilters() {
    // console.log('loadPinnedFilters')
    // const token = store.state.auth.token
    // const userId = store.state.auth.user.id
    // const response = await getPinnedUserCompanyFilterFields(token, userId)
    // // console.log(response)
    // setPinnedFilters(response.data)
}

async function togglePinnedFilter(filterKey) {
    // console.log('togglePinnedFilter', filterKey)
    let pinnedValue = emailTemplateFilters[filterKey].pinned
    emailTemplateFilters[filterKey].pinned = !emailTemplateFilters[filterKey].pinned
    reorderFilters.value = !reorderFilters.value
    if(pinnedValue) {
        await persistUnpinnedFilter(emailTemplateFilters[filterKey])
    } else {
        await persistPinnedFilter(emailTemplateFilters[filterKey])
    }
    loadPinnedFilters()
}

async function persistPinnedFilter(filter) {
    // console.log('persistPinnedFilter', filter)
    // const token = store.state.auth.token
    // const payload = JSON.stringify({
    //     company_filter_field_id: filter.id,
    //     pinned: true,
    // })
    // const response = await createPinnedUserCompanyFilterField(token, payload)
    // // console.log(response)
}

async function persistUnpinnedFilter(filter) {
    // console.log('persistUnpinnedFilter', filter)
    // const token = store.state.auth.token
    // const response = await deletePinnedUserCompanyFilterField(token, filter.persisted_id)
    // console.log(response)
}

async function loadPinnedBulkOperations() {
    // // console.log('loadPinnedBulkOperations')
    // const token = store.state.auth.token
    // const userId = store.state.auth.user.id
    // const response = await getPinnedUserCompanyBulkOperationFields(token, userId)
    // // console.log(response)
    // setPinnedBulkOperations(response.data)
}

async function togglePinnedBulkOperation(bulkOperationKey) {
    // console.log('togglePinnedBulkOperation', bulkOperationKey)
    let pinnedValue = emailTemplateBulkOperations[bulkOperationKey].pinned
    emailTemplateBulkOperations[bulkOperationKey].pinned = !emailTemplateBulkOperations[bulkOperationKey].pinned
    reorderBulkOperations.value = !reorderBulkOperations.value
    if(pinnedValue) {
        await persistUnpinnedBulkOperation(emailTemplateBulkOperations[bulkOperationKey])
    } else {
        await persistPinnedBulkOperation(emailTemplateBulkOperations[bulkOperationKey])
    }
    loadPinnedBulkOperations()
}

async function persistPinnedBulkOperation(bulk_operation) {
    // console.log('persistPinnedBulkOperation', bulk_operation)
    const token = store.state.auth.token
    const payload = JSON.stringify({
        company_bulk_operation_field_id: bulk_operation.id,
        pinned: true,
    })
    // const response = await createPinnedUserCompanyBulkOperationField(token, payload)
    // console.log(response)
}

async function persistUnpinnedBulkOperation(bulk_operation) {
    // console.log('persistUnpinnedBulkOperation', bulk_operation)
    // const token = store.state.auth.token
    // const response = await deletePinnedUserCompanyBulkOperationField(token, bulk_operation.persisted_id)
    // console.log(response)
}

async function handleBulkOperationInputChange(bulkAction, inputKey, inputValue) {
    await emailTemplateBulkOperations[bulkAction.key].inputs[inputKey].onUpdate(bulkAction, inputValue)
}

async function performEmailTemplateBulkAction(bulkActionData) {
    // console.log('performEmailTemplateBulkAction')
    // console.log(bulkActionData)
    // if(bulkActionData.overrideAction) {
    //     emailTemplateBulkOperations[bulkActionData.key].performBulkAction(bulkActionData)
    // } else {
    //     let response
    //     const token = store.state.auth.token
    //     if(bulkActionData.type == 'onSelected' && bulkActionData.selectedResults.length > 0) {
    //         const selectedResultIds = bulkActionData.selectedResults.map(({id}) => id)
    //         response = await performBulkAction(token, selectedResultIds, bulkActionData.endpoint, bulkActionData.payload)
    //     } else if (bulkActionData.type == 'onQuery' && bulkActionData.queryParams) {
    //         response = await performBulkActionOnQuery(token, bulkActionData.payload, bulkActionData.endpoint, bulkActionData.queryParams)
    //     }
    //     if(response) {
    //         handleBulkActionResponse(bulkActionData, response)
    //     }
    // }
}

function fetchAllOptions() {
    loadClientOptions()
    loadEmailAddressOptions()
    // loadEmailTemplates()
    // loadPinnedFilters()
    // loadPinnedBulkOperations()
}
  
fetchAllOptions()
</script>